import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { convertMarkdownToJSON } from '#/dashdraft/utils/data';

const PasteMarkdown = Extension.create({
  name: 'pasteMarkdown',
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('pasteMarkdown'),
        props: {
          handlePaste: (_view, event, slice) => {
            const text = event.clipboardData?.getData('text') ?? '';

            if (isMarkdown(text)) {
              convertMarkdownToJSON(text)
                .then((json) => {
                  this.editor
                    .chain()
                    .focus()
                    .insertContent(json, {
                      parseOptions: {
                        preserveWhitespace: false,
                      },
                    })
                    .run();
                })
                .catch((error) => {
                  console.error('pasteMarkdown', error);
                });
              return true;
            }
            return this.editor
              .chain()
              .focus()
              .insertContent(slice.content)
              .run();
          },
        },
      }),
    ];
  },
});

export default PasteMarkdown.configure();

/*
|------------------
| Internals
|------------------
*/

/**
 * Checks if a string is likely Markdown content
 * @param text - The string to check
 * @returns boolean - True if the string appears to be Markdown
 */
function isMarkdown(text: string): boolean {
  if (!text || typeof text !== 'string') return false;

  // Common Markdown patterns to check
  const markdownPatterns = [
    /^#{1,6}\s+.+$/m, // Headers
    /\*\*.+\*\*/, // Bold text
    /\*.+\*/, // Italic text
    /^\s*[-*+]\s+.+$/m, // Unordered lists
    /^\s*\d+\.\s+.+$/m, // Ordered lists
    /\[.+\]\(.+\)/, // Links
    /!\[.+\]\(.+\)/, // Images
    /^>\s+.+$/m, // Blockquotes
    /^```[\s\S]*?```$/m, // Code blocks
    /`[^`]+`/, // Inline code
    /^\s*---\s*$/m, // Horizontal rules
    /^\s*\|.+\|.+\|$/m, // Tables
    /~~.+~~/, // Strikethrough
  ];

  // Check if any pattern matches
  const hasMarkdownSyntax = markdownPatterns.some((pattern) =>
    pattern.test(text)
  );

  // Additional heuristics to reduce false positives
  // If text is very short but has no markdown patterns, it's probably not markdown
  if (text.length < 20 && !hasMarkdownSyntax) return false;

  // If text contains HTML but no markdown patterns, it's probably not markdown
  const hasHTML = /<\/?[a-z][\s\S]*>/i.test(text);
  if (hasHTML && !hasMarkdownSyntax) return false;

  return hasMarkdownSyntax;
}
